import { Link } from 'react-router-dom';
import type { Job } from '../shared/api';
import { COUNTY_LABELS } from '../shared/api';
import { daysLabel, daysUntil, fmtDate, money, ROLE_LABELS, toneForDeadline, TONE_TEXT } from '../lib/format';
import { DataTable, Pill, StatusBadge, Td } from './ui';

export type SortKey = 'address' | 'status' | 'deadline';

/** Soonest live deadline on a job — what the row is colour-coded against. */
export function nextDeadline(job: Job): { label: string; iso: string | null } {
  if (!job.nooSentAt && job.nooDeadline) return { label: 'NTO', iso: job.nooDeadline };
  if (!job.lienFiled && job.lienDeadline) return { label: 'Lien', iso: job.lienDeadline };
  if (job.lienEnforcementDeadline) return { label: 'Enforce', iso: job.lienEnforcementDeadline };
  return { label: '—', iso: null };
}

export function sortJobs(jobs: Job[], key: SortKey): Job[] {
  const copy = [...jobs];
  if (key === 'address') return copy.sort((a, b) => a.propertyAddress.localeCompare(b.propertyAddress));
  if (key === 'status') return copy.sort((a, b) => a.status.localeCompare(b.status));
  // Undated jobs sink to the bottom rather than sorting as "infinitely urgent".
  return copy.sort((a, b) => {
    const av = nextDeadline(a).iso ?? '9999-12-31';
    const bv = nextDeadline(b).iso ?? '9999-12-31';
    return av.localeCompare(bv);
  });
}

export default function JobsTable({ jobs, empty }: { jobs: Job[]; empty?: string }) {
  return (
    <DataTable
      head={['Property', 'Role', 'County', 'Status', 'Next deadline', 'Contract']}
      empty={empty ?? 'No jobs yet.'}
    >
      {jobs.map((job) => {
        const next = nextDeadline(job);
        const tone = toneForDeadline(next.iso);
        const days = daysUntil(next.iso);
        return (
          <tr key={job.id} className="border-b border-border/60 hover:bg-surface-alt/50">
            <Td>
              <Link to={`/jobs/${job.id}`} className="font-medium text-ink hover:text-tint">
                {job.propertyAddress}
              </Link>
              <span className="block text-xs text-dim">{job.legalOwner ?? 'Owner not verified'}</span>
            </Td>
            <Td className="text-muted">{ROLE_LABELS[job.contractorRole]}</Td>
            <Td className="text-muted">{COUNTY_LABELS[job.county]}</Td>
            <Td>
              <StatusBadge status={job.status} />
            </Td>
            <Td>
              {next.iso ? (
                <span className="flex items-center gap-2">
                  <Pill tone={tone}>{daysLabel(days)}</Pill>
                  <span className={`text-xs ${TONE_TEXT[tone]}`}>
                    {next.label} · {fmtDate(next.iso)}
                  </span>
                </span>
              ) : (
                <span className="text-xs text-dim">None</span>
              )}
            </Td>
            <Td className="text-muted">{money(job.contractAmount)}</Td>
          </tr>
        );
      })}
    </DataTable>
  );
}
